import type { ButtonBackground, ButtonColor, ButtonSize, ButtonStyle } from '@tranquility-ai/theme-components/button';

/**
 * =============================================================================
 * STATUS-BAR ICON BUTTON CONTRACT — DO NOT "IMPROVE" THIS FILE
 * Cursor / Claude / other agents: STOP. This chrome has regressed many times.
 * Read `.claude/skills/status-bar-icon-buttons/SKILL.md` before touching
 * status-bar triggers. Do not add className overrides, flatten the fill, or
 * switch glyphs to `weight="duotone"`.
 * =============================================================================
 *
 * Shared chrome for status-bar icon triggers (backlog/sync, analytics, tools,
 * prototypes, case files, employee switcher). Circular `sm` icon-only theme Buttons with
 * `background="inverse"` and a single-color (regular) Phosphor glyph.
 *
 * Required props — spread `{...statusBarIconButtonProps}` and do not override
 * `iconOnly`, `size`, `style`, `color`, or `background`.
 *
 * Required className — `className={statusBarIconButtonClassName}` only.
 */

export const statusBarIconButtonProps = {
  iconOnly: true,
  size: 'sm' as ButtonSize,
  style: 'text' as ButtonStyle,
  color: 'light' as ButtonColor,
  background: 'inverse' as ButtonBackground,
} as const;

/**
 * Keep glyphs at 22px via the Button icon-size token so the sm slot stays
 * square and hover fill is a circle — never set child `w-*`/`h-*` on
 * `.tp-button-icon` (that produces ovals / pills).
 */
export const statusBarIconButtonClassName =
  'text-white ![--tp-button-icon-size:22px]';

/**
 * Tailwind fragments that MUST NOT appear on status-bar icon `Button` triggers.
 * Agents often add these when migrating HeroUI/raw `<button>` markup — they
 * strip the circular inverse hover chrome and produce flat / two-tone controls.
 */
export const STATUS_BAR_ICON_BUTTON_FORBIDDEN_CLASS_FRAGMENTS = [
  'bg-transparent',
  'hover:!bg-transparent',
  'active:!bg-transparent',
  'border-none',
  'opacity-60',
  'opacity-70',
  'hover:opacity-100',
] as const;

/**
 * `<a>` triggers (EmployeeSwitcher) cannot use theme Button; mirror the same
 * token-driven chrome via data-* attrs + the shared tp-button base class.
 */
export const statusBarIconLinkClassName = `tp-button inline-flex select-none cursor-pointer items-center justify-center whitespace-nowrap font-medium border border-solid gap-[var(--tp-button-gap)] rounded-[var(--tp-button-radius)] px-[var(--tp-button-pad-x)] py-[var(--tp-button-pad-y)] text-[length:var(--tp-button-font-size)] leading-none transition-[background-color,color,border-color] duration-150 ${statusBarIconButtonClassName}`;
